import { Inject, Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, defer, fromEvent, throwError } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

export const WINDOW = new InjectionToken<Window>(
  'An abstraction over global window object',
  {
    factory: () => {
      const { defaultView } = inject(DOCUMENT);

      if (!defaultView) {
        throw new Error('Window is not available');
      }

      return defaultView;
    }
  }
);

// const WINDOW = new InjectionToken<Window>('Window object', {

export const NOTIFICATION_SUPPORT = new InjectionToken<boolean>(
  'Is Notification API supported?',
  {
    factory: () => 'Notification' in inject(WINDOW)
  }
);

const NOT_SUPPORTED_ERROR$ = throwError(
  () => new Error('Notification API is not supported in your browser')
);

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  constructor(
    @Inject(NOTIFICATION_SUPPORT) private readonly support: boolean
  ) {}

  requestPermission(): Observable<NotificationPermission> {
    if (!this.support) {
      return NOT_SUPPORTED_ERROR$;
    }

    /**
     * TODO: replace deprecated callback with promise after Safari 15+ support
     * return from(Notification.requestPermission());
     */
    return new Observable(subscriber => {
      void Notification.requestPermission(permission => {
        subscriber.next(permission);
        subscriber.complete();
      })?.catch(err => subscriber.error(err));
    });
  }

  open(title: string, options?: NotificationOptions): Observable<Notification> {
    if (!this.support) {
      return NOT_SUPPORTED_ERROR$;
    }

    return defer(() => {
      const notification = new Notification(title, options);
      const close$ = fromEvent(notification, 'close');

      return new Observable<Notification>(subscriber => {
        subscriber.next(notification);

        return () => notification.close();
      }).pipe(takeUntil(close$));
    });
  }
}
